import { useState } from 'react';
import { Check } from 'lucide-react';
import { useQueryClient } from '@tanstack/react-query';
import { useT } from '../i18n';
import { getApiClient } from '../api/factory';
import { mergeConversation, patchConversation, type Conversation } from '../api/conversations';

// Кнопка «Закрыть диалог» в шапке диалога. Раньше закрытие жило только на
// хоткее `e` (недискаверабельно — оператор не видел способа закрыть диалог).
// Переоткрытие оператором бэкендом не поддерживается (PATCH принимает только
// status='closed'; reopen — visitor-driven, ANX-075), поэтому для закрытого
// диалога показываем статичный индикатор статуса, а не кнопку.
export interface CloseControlProps {
  conv: Conversation;
  conversationId: string;
}

export function CloseControl({ conv, conversationId }: CloseControlProps) {
  const t = useT();
  const queryClient = useQueryClient();
  const [pending, setPending] = useState(false);

  if (conv.status === 'closed') {
    return (
      <span className="inline-flex items-center gap-1 rounded-md border border-slate-200 px-2 py-1 text-slate-400">
        <Check size={14} aria-hidden="true" />
        <span>{t('conversation.closed')}</span>
      </span>
    );
  }

  async function close() {
    if (pending) return;
    setPending(true);
    try {
      const updated = await patchConversation(getApiClient(), conversationId, {
        status: 'closed',
      });
      queryClient.setQueryData<Conversation>(['conversation', conversationId], (prev) =>
        mergeConversation(prev, updated),
      );
      void queryClient.invalidateQueries({ queryKey: ['conversations'] });
    } catch {
      // Сетевой сбой — оператор всегда может повторить кликом.
    } finally {
      setPending(false);
    }
  }

  return (
    <button
      type="button"
      onClick={() => void close()}
      disabled={pending}
      aria-label={t('conversation.close')}
      className="inline-flex items-center gap-1 rounded-md border border-emerald-200 bg-emerald-50 px-2 py-1 text-emerald-700 hover:bg-emerald-100 disabled:opacity-50"
    >
      <Check size={14} aria-hidden="true" />
      <span>{t('conversation.close')}</span>
    </button>
  );
}
